import React, { useState, useMemo, useEffect, useDeferredValue } from 'react';
import { format } from 'date-fns';
import { useIsMobile } from '../hooks';
import { BarChart3 as BarChart3Icon, Hourglass as HourglassIcon } from 'lucide-react';

/**
 * DynamicReportsTable Component
 * 
 * A professional, flexible table component for displaying reports with:
 * - Dynamic columns based on data structure
 * - Nested data handling (JSON strings)
 * - Expandable rows for complex objects
 * - Sorting, filtering, and pagination (only one page of rows is ever in the DOM:
 *   large reports used to render every row x every column and froze browsers)
 * - Export to CSV/Excel
 * - Automatic timezone conversion to IST (Indian Standard Time)
 */

/**
 * Convert UTC date to Indian Standard Time (IST) and format
 * @param {string|Date} dateValue - UTC date string or Date object
 * @param {boolean} includeTime - Whether to include time in output
 * @returns {string} Formatted date/datetime in IST
 */
const formatDateToIST = (dateValue, includeTime = false) => {
  try {
    const date = new Date(dateValue);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateValue;
    }
    
    // Format options for IST (Asia/Kolkata)
    const options = {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      ...(includeTime && {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    };
    
    // Use Intl.DateTimeFormat for timezone conversion
    const formatter = new Intl.DateTimeFormat('en-IN', options);
    return formatter.format(date);
  } catch (error) {
    console.warn('Date formatting error:', error);
    return dateValue;
  }
};

const DynamicReportsTable = ({ 
  data = [], 
  columns = [], 
  loading = false,
  onExport,
  showPagination = true,
  itemsPerPageOptions = [25, 50, 100, 250],
  defaultItemsPerPage = 50,
  title = "Report",
  disableSorting = false,
  emptyTitle = 'No Data Available',
  emptyMessage = 'Try adjusting your filters or refresh the report'
}) => {
  const isMobile = useIsMobile();
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
  const [searchTerm, setSearchTerm] = useState('');
  // typing stays responsive; the (whole-dataset) filter runs on the deferred value
  const deferredSearch = useDeferredValue(searchTerm);

  // Reset to page 1 when data changes (e.g., filters applied or another month loaded)
  useEffect(() => {
    setCurrentPage(1);
    setExpandedRows(new Set());
  }, [data]);

  // Toggle row expansion for nested data
  const toggleRowExpansion = (rowIndex) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowIndex)) {
      newExpanded.delete(rowIndex);
    } else {
      newExpanded.add(rowIndex);
    }
    setExpandedRows(newExpanded);
  };

  // Format cell value based on type
  const formatValue = (value, column) => {
    if (value === null || value === undefined) return '-';
    
    // Handle arrays - join with comma (check before object check since arrays are objects)
    if (Array.isArray(value)) {
      return value.join(', ');
    }

    // Handle objects FIRST - must check before date formatting to catch empty date objects
    if (typeof value === 'object') {
      // Check if it's an empty object (backend sends {} for missing dates)
      if (Object.keys(value).length === 0) {
        return '-';
      }
      return '[Object]';
    }
    
    // Handle custom formatter
    if (column.formatter) {
      const formatted = column.formatter(value);
      // Ensure formatter doesn't return an object
      if (formatted !== null && formatted !== undefined && typeof formatted === 'object') {
        return JSON.stringify(formatted);
      }
      return formatted;
    }

    // Auto-detect and format dates (date only, no time) - Convert to IST
    if (column.type === 'date' || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value) && column.type !== 'datetime')) {
      return formatDateToIST(value, false);
    }

    // Auto-detect and format datetime (with time) - Convert to IST
    if (column.type === 'datetime' || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value))) {
      return formatDateToIST(value, true);
    }

    // Format currency
    if (column.type === 'currency') {
      return `₹${parseFloat(value || 0).toFixed(2)}`;
    }

    // Format percentage
    if (column.type === 'percentage') {
      return `${parseFloat(value || 0).toFixed(2)}%`;
    }

    // Handle JSON strings - detect and badge
    if (typeof value === 'string' && (value.startsWith('[') || value.startsWith('{'))) {
      try {
        JSON.parse(value);
        return '[JSON Data]';
      } catch {
        return value;
      }
    }

    return value;
  };

  // Parse JSON string fields
  const parseJsonField = (value) => {
    if (!value) return null;
    if (typeof value === 'string' && (value.startsWith('[') || value.startsWith('{'))) {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }
    if (typeof value === 'object') return value;
    return null;
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortColumn) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortColumn.key];
      const bVal = b[sortColumn.key];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      const multiplier = sortDirection === 'asc' ? 1 : -1;

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * multiplier;
      }

      return String(aVal).localeCompare(String(bVal)) * multiplier;
    });
  }, [data, sortColumn, sortDirection]);

  // Lower-cased text of each row's visible columns, built once per row and reused
  // for every keystroke (rows are stable objects, so a WeakMap is a free cache)
  const rowText = useMemo(() => {
    const cache = new WeakMap();
    return (row) => {
      let text = cache.get(row);
      if (text === undefined) {
        text = columns
          .map(column => {
            const value = row[column.key];
            return value === null || value === undefined ? '' : String(value);
          })
          .join('\u0001')
          .toLowerCase();
        cache.set(row, text);
      }
      return text;
    };
  }, [columns]);

  // Filter data by search term
  const filteredData = useMemo(() => {
    const needle = deferredSearch.trim().toLowerCase();
    if (!needle) return sortedData;
    return sortedData.filter(row => rowText(row).includes(needle));
  }, [sortedData, deferredSearch, rowText]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!showPagination) return filteredData;
    
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage, showPagination]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  // Handle sort
  const handleSort = (column) => {
    if (sortColumn?.key === column.key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Render nested data in expanded row
  const renderNestedData = (value) => {
    const parsed = parseJsonField(value);
    if (!parsed) return null;

    if (Array.isArray(parsed)) {
      if (parsed.length === 0) return <div style={{ padding: '8px', color: '#5F6B61' }}>No items</div>;
      
      return (
        <div style={{ padding: '12px', backgroundColor: '#F5F3EB', borderRadius: '6px' }}>
          <div style={{ fontWeight: '600', marginBottom: '8px', color: '#36433B' }}>
            {parsed.length} item{parsed.length !== 1 ? 's' : ''}
          </div>
          {parsed.map((item, idx) => (
            <div key={idx} style={{
              padding: '10px',
              backgroundColor: 'white',
              borderRadius: '4px',
              marginBottom: '8px',
              border: '1px solid #E3DFD3',
              fontSize: '13px'
            }}>
              {typeof item === 'object' ? (
                Object.entries(item).map(([key, val]) => (
                  <div key={key} style={{ display: 'flex', marginBottom: '4px' }}>
                    <span style={{ fontWeight: '600', minWidth: '120px', color: '#5F6B61' }}>
                      {key}:
                    </span>
                    <span style={{ color: '#1A3E2A' }}>{formatValue(val, {})}</span>
                  </div>
                ))
              ) : (
                <span>{String(item)}</span>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (typeof parsed === 'object') {
      return (
        <div style={{ padding: '12px', backgroundColor: '#F5F3EB', borderRadius: '6px' }}>
          {Object.entries(parsed).map(([key, val]) => (
            <div key={key} style={{ display: 'flex', marginBottom: '6px', fontSize: '13px' }}>
              <span style={{ fontWeight: '600', minWidth: '150px', color: '#5F6B61' }}>
                {key}:
              </span>
              <span style={{ color: '#1A3E2A' }}>{formatValue(val, {})}</span>
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  // Check if field has nested data
  const hasNestedData = (value) => {
    const parsed = parseJsonField(value);
    return parsed !== null;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '60px',
        fontSize: '16px',
        color: '#5F6B61'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}><HourglassIcon size="1em" aria-hidden="true" /></div>
          <div>Loading {title}...</div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        backgroundColor: '#F5F3EB',
        borderRadius: '8px',
        border: '1px dashed #D2CDBF'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '12px' }}><BarChart3Icon size="1em" aria-hidden="true" /></div>
        <div style={{ fontSize: '16px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
          {emptyTitle}
        </div>
        <div style={{ fontSize: '14px', color: '#5F6B61' }}>
          {emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', border: '1px solid #E3DFD3' }}>
      {/* Toolbar */}
      <div style={{
        padding: isMobile ? '12px' : '16px 20px',
        borderBottom: '1px solid #E3DFD3',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: '16px',
        flexWrap: 'wrap',
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        {/* Search */}
        <div style={{ flex: '1', minWidth: '200px', maxWidth: isMobile ? '100%' : '400px', width: isMobile ? '100%' : 'auto' }}>
          <input
            type="text"
            placeholder="Search across all columns..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D2CDBF',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>

        {/* Info & Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
          <span style={{ fontSize: '14px', color: '#5F6B61' }}>
            {filteredData.length} record{filteredData.length !== 1 ? 's' : ''}
          </span>
          {onExport && (
            <button
              onClick={onExport}
              style={{
                padding: '8px 16px',
                backgroundColor: '#1A3E2A',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div style={{ overflow: 'auto', maxHeight: '70vh' }}>
        <table style={{
          width: 'max-content',
          minWidth: '100%',
          borderCollapse: 'collapse',
          fontSize: '14px'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#F5F3EB', borderBottom: '2px solid #E3DFD3' }}>
              <th style={{
                padding: '12px 16px',
                textAlign: 'left',
                fontWeight: '600',
                color: '#36433B',
                width: '40px',
                position: 'sticky',
                top: 0,
                zIndex: 4,
                backgroundColor: '#F5F3EB'
              }}>
                #
              </th>
              {columns.map((column) => (
                <th
                  key={column.key}
                  onClick={() => !disableSorting && column.sortable !== false && handleSort(column)}
                  style={{
                    padding: '12px 16px',
                    textAlign: column.align || 'left',
                    fontWeight: '600',
                    color: '#36433B',
                    cursor: !disableSorting && column.sortable !== false ? 'pointer' : 'default',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                    minWidth: column.minWidth || 'auto',
                    opacity: disableSorting ? 0.8 : 1,
                    position: 'sticky',
                    top: 0,
                    zIndex: 4,
                    backgroundColor: '#F5F3EB'
                  }}
                >
                  {column.label}
                  {!disableSorting && sortColumn?.key === column.key && (
                    <span style={{ marginLeft: '6px' }}>
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
              ))}
              <th style={{
                padding: '12px 16px',
                textAlign: 'center',
                fontWeight: '600',
                color: '#36433B',
                width: '60px',
                position: 'sticky',
                top: 0,
                zIndex: 4,
                backgroundColor: '#F5F3EB'
              }}>
                Details
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, rowIndex) => {
              const actualIndex = (currentPage - 1) * itemsPerPage + rowIndex;
              const isExpanded = expandedRows.has(actualIndex);
              const rowHasNestedData = columns.some(col => hasNestedData(row[col.key]));

              return (
                <React.Fragment key={actualIndex}>
                  <tr
                    style={{
                      borderBottom: '1px solid #E3DFD3',
                      backgroundColor: rowIndex % 2 === 0 ? 'white' : '#F5F3EB'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#EEEBE1'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = rowIndex % 2 === 0 ? 'white' : '#F5F3EB'}
                  >
                    <td style={{ padding: '12px 16px', color: '#5F6B61', fontWeight: '500' }}>
                      {actualIndex + 1}
                    </td>
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        style={{
                          padding: '12px 16px',
                          textAlign: column.align || 'left',
                          color: '#1A3E2A',
                          whiteSpace: column.wrap ? 'normal' : 'nowrap',
                          maxWidth: column.maxWidth || 'auto'
                        }}
                      >
                        {formatValue(row[column.key], column)}
                      </td>
                    ))}
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {rowHasNestedData && (
                        <button
                          onClick={() => toggleRowExpansion(actualIndex)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: isExpanded ? '#1A3E2A' : '#E3DFD3',
                            color: isExpanded ? 'white' : '#36433B',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          {isExpanded ? '▲' : '▼'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={columns.length + 2} style={{ padding: '0', backgroundColor: '#F5F3EB' }}>
                        <div style={{ padding: '16px 20px', borderTop: '1px solid #E3DFD3' }}>
                          <div style={{ fontWeight: '600', marginBottom: '12px', color: '#36433B', fontSize: '15px' }}>
                            Detailed Information
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                            {columns.map((column) => {
                              const nestedData = renderNestedData(row[column.key]);
                              if (!nestedData) return null;
                              
                              return (
                                <div key={column.key}>
                                  <div style={{ fontWeight: '600', marginBottom: '8px', color: '#5F6B61', fontSize: '13px' }}>
                                    {column.label}
                                  </div>
                                  {nestedData}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {showPagination && totalPages > 1 && (
        <div style={{
          padding: isMobile ? '12px' : '16px 20px',
          borderTop: '1px solid #E3DFD3',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          flexWrap: 'wrap',
          gap: '12px',
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          {/* Items per page */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', color: '#5F6B61' }}>Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '6px 10px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              {itemsPerPageOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <span style={{ fontSize: '14px', color: '#5F6B61' }}>per page</span>
          </div>

          {/* Page navigation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', color: '#5F6B61', paddingRight: '8px' }}>
              {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length}
            </span>
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              style={{
                padding: '6px 12px',
                backgroundColor: currentPage === 1 ? '#EEEBE1' : 'white',
                color: currentPage === 1 ? '#A5A89B' : '#36433B',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '6px 12px',
                backgroundColor: currentPage === 1 ? '#EEEBE1' : 'white',
                color: currentPage === 1 ? '#A5A89B' : '#36433B',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              ← Prev
            </button>
            <span style={{ fontSize: '14px', color: '#36433B', fontWeight: '500', padding: '0 8px' }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '6px 12px',
                backgroundColor: currentPage === totalPages ? '#EEEBE1' : 'white',
                color: currentPage === totalPages ? '#A5A89B' : '#36433B',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              Next →
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              style={{
                padding: '6px 12px',
                backgroundColor: currentPage === totalPages ? '#EEEBE1' : 'white',
                color: currentPage === totalPages ? '#A5A89B' : '#36433B',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicReportsTable;
